import {
  insertApprovalInputSchema,
  updateApprovalInputSchema,
} from '@risksmart-app/trpc/src/routers/frontend/approval.schema';

import type {
  InsertApprovalMutationVariables,
  UpdateApprovalMutationVariables,
} from '../nativeMutationInputs';

/**
 * Maps the GraphQL approval mutation variables (the objects the settings
 * modals already build) to the flat, PascalCase tRPC inputs.
 *
 * GraphQL's `*_insert_input` / `_set` variable types are wire-nullable on
 * every field while the tRPC contract requires most of them — instead of
 * hand-rolled narrowing, the mapped objects are `.parse()`d against the SAME
 * zod schemas the tRPC router enforces (`approval.schema.ts`, a pure-zod leaf
 * module). That narrows the types, surfaces the server's own validation
 * messages client-side (including the exactly-one-of approver-target rule),
 * and cannot drift from the backend contract.
 */
const mapApprover = (approver: {
  UserId?: string | null;
  UserGroupId?: string | null;
  OwnerApprover?: boolean | null;
}) => ({
  UserId: approver.UserId ?? null,
  UserGroupId: approver.UserGroupId ?? null,
  OwnerApprover: approver.OwnerApprover ?? null,
});

/**
 * Nested GraphQL `approval_insert_input` → tRPC `frontend.approval.insert`
 * input.
 */
export const mapInsertApprovalVariables = (
  approval: InsertApprovalMutationVariables['approval']
) =>
  insertApprovalInputSchema.parse({
    ParentId: approval.ParentId ?? null,
    Workflow: approval.Workflow,
    InFlightEditRule: approval.InFlightEditRule,
    Levels: (approval.levels?.data ?? []).map((level) => ({
      Description: level.Description,
      SequenceOrder: level.SequenceOrder,
      ApprovalRuleType: level.ApprovalRuleType,
      Approvers: (level.approvers?.data ?? []).map(mapApprover),
    })),
  });

/**
 * GraphQL update diff variables → tRPC `frontend.approval.update` input. The
 * GraphQL `updateLevels[]` carries a `where/_set` shape and the
 * `insertLevels[]` carry nested `approvers.data` — both are flattened here.
 */
export const mapUpdateApprovalVariables = (
  variables: UpdateApprovalMutationVariables
) =>
  updateApprovalInputSchema.parse({
    Id: variables.Id,
    Approval: {
      Workflow: variables.approval.Workflow,
      InFlightEditRule: variables.approval.InFlightEditRule,
      // No ParentId — re-parenting is not supported server-side.
    },
    DeleteLevelIds: Array.isArray(variables.deleteLevelIds)
      ? variables.deleteLevelIds
      : [variables.deleteLevelIds],
    DeleteApproverIds: Array.isArray(variables.deleteApproverIds)
      ? variables.deleteApproverIds
      : [variables.deleteApproverIds],
    UpdateLevels: (Array.isArray(variables.updateLevels)
      ? variables.updateLevels
      : [variables.updateLevels]
    ).map((update) => ({
      Id: update.where.Id?._eq,
      Description: update._set?.Description,
      SequenceOrder: update._set?.SequenceOrder,
      ApprovalRuleType: update._set?.ApprovalRuleType,
    })),
    InsertLevels: (Array.isArray(variables.insertLevels)
      ? variables.insertLevels
      : [variables.insertLevels]
    ).map((level) => ({
      Description: level.Description,
      SequenceOrder: level.SequenceOrder,
      ApprovalRuleType: level.ApprovalRuleType,
      Approvers: (level.approvers?.data ?? []).map(mapApprover),
    })),
    InsertApprovers: (Array.isArray(variables.insertApprovers)
      ? variables.insertApprovers
      : [variables.insertApprovers]
    ).map((approver) => ({
      LevelId: approver.LevelId,
      ...mapApprover(approver),
    })),
  });
